import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PreviewRow {
  organizationId: string;
  organizationName: string;
  currentHuduCompanyId: number | null;
  match: { huduCompanyId: number; huduName: string; huduUrl: string | null } | null;
  ambiguous: boolean;
}

interface PreviewResponse {
  huduCompanyCount: number;
  orgCount: number;
  preview: PreviewRow[];
}

export function AdminHuduPage(): React.ReactElement {
  useDocumentTitle('Hudu integration');
  const { agent } = useAuth();

  const [configured, setConfigured] = useState<boolean | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ configured: boolean }>('/api/admin/hudu/status')
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(false));
  }, []);

  async function loadPreview() {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const r = await apiFetch<PreviewResponse>('/api/admin/hudu/match-preview');
      setPreview(r);
      // Default-select every unambiguous match where the org doesn't
      // already have a hudu_company_id set.
      const next = new Set<string>();
      for (const row of r.preview) {
        if (row.match && row.currentHuduCompanyId === null) {
          next.add(row.organizationId);
        }
      }
      setSelected(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load preview.');
    } finally {
      setLoading(false);
    }
  }

  async function applyMatches() {
    if (!preview) return;
    const pairs = preview.preview
      .filter((r) => selected.has(r.organizationId) && r.match)
      .map((r) => ({
        organizationId: r.organizationId,
        huduCompanyId: r.match!.huduCompanyId,
        huduCompanyUrl: r.match!.huduUrl ?? null,
      }));
    if (pairs.length === 0) {
      setError('Nothing selected to apply.');
      return;
    }
    setApplying(true);
    setError(null);
    setInfo(null);
    try {
      const r = await apiFetch<{ applied: number; skipped: number; profilesSeeded?: number }>(
        '/api/admin/hudu/apply-matches',
        { method: 'POST', json: { pairs, overwrite } },
      );
      setInfo(
        `Applied ${r.applied}. Skipped ${r.skipped}.` +
          (typeof r.profilesSeeded === 'number'
            ? ` Pre-seeded ${r.profilesSeeded} org profile${r.profilesSeeded === 1 ? '' : 's'} from T3 Top Info.`
            : ''),
      );
      await loadPreview();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Apply failed.');
    } finally {
      setApplying(false);
    }
  }

  function toggle(orgId: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Hudu integration</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted)]">
          Match T3 organizations to their Hudu company so each org page deep-links into Hudu.
          Matching is by case-insensitive name. Ambiguous (more than one Hudu company with the
          same name) and unmatched orgs are skipped — fix the name on either side and re-run.
          Applying a match also pre-seeds the org profile from Hudu's "T3 Top Info" asset
          (RMM, ticketing system, main POC, app shared mailbox, password manager, check-in
          frequency) — fill-empty semantics, so manual edits are never overwritten.
        </p>
      </div>

      {configured === false && (
        <Card className="border-amber-300 bg-amber-50/40">
          <p className="text-sm text-amber-900">
            Hudu integration is not configured. Set <code>HUDU_BASE_URL</code> and{' '}
            <code>HUDU_API_KEY</code> in your environment, then restart the backend.
          </p>
        </Card>
      )}

      {configured && (
        <Card>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void loadPreview()} disabled={loading}>
              {loading ? 'Loading…' : preview ? 'Re-run match preview' : 'Run match preview'}
            </Button>
            {preview && (
              <span className="text-sm text-[var(--color-muted)]">
                {preview.huduCompanyCount} Hudu companies · {preview.orgCount} T3 orgs
              </span>
            )}
          </div>
        </Card>
      )}

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}
      {info && (
        <Card className="border-emerald-300 bg-emerald-50/40">
          <p className="text-sm text-emerald-900">{info}</p>
        </Card>
      )}

      {preview && (
        <Card className="overflow-hidden !p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-sm">
            <span>
              {selected.size} selected · {preview.preview.filter((r) => r.match).length} matchable
            </span>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                className="h-4 w-4"
              />
              Overwrite already-mapped orgs
            </label>
            <Button onClick={() => void applyMatches()} disabled={applying || selected.size === 0}>
              {applying ? 'Applying…' : `Apply ${selected.size}`}
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">T3 org</th>
                <th className="px-4 py-3">Current Hudu id</th>
                <th className="px-4 py-3">Match</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.preview.map((r) => {
                const canSelect = !!r.match;
                return (
                  <tr key={r.organizationId} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        disabled={!canSelect}
                        checked={selected.has(r.organizationId)}
                        onChange={() => toggle(r.organizationId)}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)] disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-4 py-2 font-medium">{r.organizationName}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.currentHuduCompanyId ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      {r.match ? (
                        <span>
                          <span className="font-mono text-xs">#{r.match.huduCompanyId}</span>{' '}
                          <span className="text-[var(--color-muted)]">{r.match.huduName}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--color-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {r.ambiguous ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                          ambiguous
                        </span>
                      ) : !r.match ? (
                        <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
                          no match
                        </span>
                      ) : r.currentHuduCompanyId === r.match.huduCompanyId ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          already linked
                        </span>
                      ) : r.currentHuduCompanyId !== null ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                          would overwrite
                        </span>
                      ) : (
                        <span className="rounded-full bg-[var(--color-t3-blue)]/10 px-2 py-0.5 text-xs text-[var(--color-t3-blue)]">
                          new link
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
